import { Button } from "@/components/ui/button"
import { Reveal } from "@/components/ui/Reveal"
import { SpotlightCard } from "@/components/ui/SpotlightCard"
import { Heading, Text } from "@/components/ui/typography"
import { sfIcons } from '@/lib/sf-icons'
import { cn } from "@/lib/utils"
import { semanticTokens } from "@supabank/config/theme"
import { useTranslation } from "react-i18next"

// Icon mapping for personas
const iconMap = {
  globe: sfIcons.global2,
  building: sfIcons.building2,
  trendingUp: sfIcons.trendingUp,
} as const

export function AudienceSection({ id }: { id?: string }) {
  const { t } = useTranslation("marketing")

  // Personas from SSOT (marketing.json) Primary personas (Maya, David, Alex)
  const audienceTiles = [
    {
      id: "global-earners",
      title: t("audience.globalEarners.title"),
      description: t("audience.globalEarners.description"),
      benefits: ((t("audience.globalEarners.benefits", { returnObjects: true }) as string[]) || []),
      ctaHref: t("audience.globalEarners.ctaHref"),
      icon: iconMap[t("audience.globalEarners.icon") as keyof typeof iconMap] || sfIcons.global2,
    },
    {
      id: "finance-teams",
      title: t("audience.financeTeams.title"),
      description: t("audience.financeTeams.description"),
      benefits: ((t("audience.financeTeams.benefits", { returnObjects: true }) as string[]) || []),
      ctaHref: t("audience.financeTeams.ctaHref"),
      icon: iconMap[t("audience.financeTeams.icon") as keyof typeof iconMap] || sfIcons.building2,
    },
    {
      id: "crypto-businesses",
      title: t("audience.cryptoBusinesses.title"),
      description: t("audience.cryptoBusinesses.description"),
      benefits: ((t("audience.cryptoBusinesses.benefits", { returnObjects: true }) as string[]) || []),
      ctaHref: t("audience.cryptoBusinesses.ctaHref"),
      icon: iconMap[t("audience.cryptoBusinesses.icon") as keyof typeof iconMap] || sfIcons.trendingUp,
    },
  ] as const
  return (
    <section
      id={id}
      aria-labelledby="promo-audience-title"
      className={cn("relative py-12 xs:py-14 sm:py-24 overflow-hidden", "outline-none", semanticTokens.bg.surface)}
      tabIndex={-1}
    >
      {/* Color Lab: Subtle Blue-Lavender gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 10% 90%, hsla(217, 91%, 60%, 0.10) 0%, transparent 45%),
            radial-gradient(ellipse at 90% 10%, hsla(270, 95%, 75%, 0.08) 0%, transparent 45%)
          `
        }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-4 xs:px-4 sm:px-6">
        <Reveal>
          <div className="space-y-4">
            <Text variant="caption" color="muted" className="uppercase tracking-widest font-medium">
              {t("audienceSection.badge")}
            </Text>
            <Heading level={2} id="promo-audience-title">
              {t("audienceSection.headline")}
              {t("audienceSection.headlineHighlight") && (
                <>
                  {" "}
                  <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                    {t("audienceSection.headlineHighlight")}
                  </span>
                </>
              )}
            </Heading>
            {t("audienceSection.description") && (
              <Text variant="lg" color="muted" className="max-w-2xl">
                {t("audienceSection.description")}
              </Text>
            )}
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 xs:grid-cols-1 gap-6 xs:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {audienceTiles.map((tile, idx) => {
            const Icon = tile.icon || sfIcons.global2
            if (!Icon) return null
            return (
              <Reveal key={tile.id} delay={idx * 0.1} className="h-full">
                <SpotlightCard
                  className={cn(
                    "p-8 h-full",
                    semanticTokens.motion.standard,
                    "hover:shadow-xl hover:-translate-y-1"
                  )}
                >
                  <div className="flex flex-col justify-between h-full">
                    <div className="space-y-6">
                      <div className={cn(
                        "inline-flex h-14 w-14 items-center justify-center rounded-xl transition-colors",
                        "border-2 border-border text-muted-foreground group-hover:border-border/80 group-hover:text-foreground"
                      )}>
                        <Icon className={cn("h-7 w-7")} aria-hidden="true" />
                      </div>
                      <div className="space-y-3">
                        <Heading level={3}>{tile.title}</Heading>
                        <Text color="muted" className="leading-relaxed">{tile.description}</Text>
                      </div>
                    </div>
                    <Button
                      asChild
                      variant="ghost"
                      className={cn(
                        "mt-8 px-0 justify-start hover:bg-transparent",
                        "hover:text-primary",
                        semanticTokens.focus.ring,
                        "touch-manipulation min-h-[44px]"
                      )}
                      aria-label={`${t("cta.learnMore")} about ${tile.title}`}
                    >
                      <a href={tile.ctaHref} className="text-primary font-semibold">
                        {t("cta.learnMore")}
                        <sfIcons.next className="ml-2 h-4 w-4" aria-hidden="true" />
                      </a>
                    </Button>
                  </div>
                </SpotlightCard>
              </Reveal>
            )
          })}
        </div>
      </div>
    </section>
  )
}
